/**
 * Módulo de asistencias de equipo — registro de ubicaciones diarias, asistencia masiva y consultas históricas
 */

import { createCrudMethods } from '../base/crudMethods';

/**
 * Crea el módulo de asistencias de equipo
 *
 * @param {Object} deps - Dependencias inyectadas
 * @param {Function} deps.authenticatedRequest - Función de request autenticada
 * @returns {Object} Métodos del módulo asistencias
 */
export function createAsistenciasModule({ authenticatedRequest }) {
  return {
    ...createCrudMethods(
      {
        endpoint: '/api/asistencias-equipo',
        entityName: 'asistenciaEquipo',
        entityNamePlural: 'asistenciasEquipo',
        methods: { delete: false },
        methodNames: { getById: 'getAsistenciaEquipo' }
      },
      { authenticatedRequest }
    ),

    /**
     * Registra asistencia masiva para varios equipos
     *
     * @param {Object} data - Datos del registro masivo
     * @param {number[]} data.equipo_ids - IDs de equipos
     * @param {number} data.ubicacion_id - ID de la ubicación
     * @param {string} data.fecha - Fecha en formato YYYY-MM-DD
     * @returns {Promise<{success: boolean, data: {created: number, updated: number, errors: Array}}>}
     */
    async createAsistenciaMasiva(data) {
      return await authenticatedRequest('/api/asistencias-equipo/masivo', {
        method: 'POST',
        body: JSON.stringify(data)
      });
    },

    /**
     * Obtiene equipos sin asistencia registrada en una fecha
     *
     * @param {string} fecha - Fecha en formato YYYY-MM-DD
     * @param {Object} [params] - Parámetros de paginación
     * @param {number} [params.page] - Número de página
     * @param {number} [params.limit] - Items por página
     * @returns {Promise<{success: boolean, data: Array, pagination: Object}>}
     */
    async getEquiposSinAsistencia(fecha, params = {}) {
      const queryParams = new URLSearchParams({ ...params, fecha }).toString();
      return await authenticatedRequest(`/api/asistencias-equipo/sin-asistencia?${queryParams}`);
    },

    /**
     * Obtiene resumen de cantidad de equipos por ubicación/faena en una fecha
     *
     * @param {string} [fecha] - Fecha en formato YYYY-MM-DD (default: hoy)
     * @returns {Promise<{success: boolean, data: Array<{ubicacion_id: number, codigo: string, nombre: string, cantidad_equipos: number}>}>}
     */
    async getAsistenciasResumenPorUbicacion(fecha) {
      const params = fecha ? `?fecha=${fecha}` : '';
      return await authenticatedRequest(`/api/asistencias-equipo/stats/por-ubicacion${params}`);
    },

    /**
     * Obtiene historial de ubicaciones de un equipo
     *
     * @param {number} equipoId - ID del equipo
     * @param {number} [limit] - Límite de registros
     * @returns {Promise<{success: boolean, data: Array}>}
     */
    async getHistorialUbicacionEquipo(equipoId, limit) {
      const params = limit ? `?limit=${limit}` : '';
      return await authenticatedRequest(`/api/asistencias-equipo/equipo/${equipoId}/historial${params}`);
    },

    /**
     * Obtiene ubicación de un equipo en una fecha específica
     *
     * @param {number} equipoId - ID del equipo
     * @param {string} [fecha] - Fecha en formato YYYY-MM-DD (default: hoy)
     * @returns {Promise<{success: boolean, data: Object|null}>}
     */
    async getUbicacionEquipoEnFecha(equipoId, fecha) {
      const params = fecha ? `?fecha=${fecha}` : '';
      return await authenticatedRequest(`/api/asistencias-equipo/equipo/${equipoId}/ubicacion${params}`);
    },

    /**
     * Obtiene datos de asistencias en un rango de fechas para exportación Excel
     *
     * @param {Object} params
     * @param {string} params.fecha_inicio - Fecha inicio formato YYYY-MM-DD
     * @param {string} params.fecha_fin - Fecha fin formato YYYY-MM-DD
     * @returns {Promise<{success: boolean, data: {equipos: Array, asistencias: Array}}>}
     */
    async getAsistenciasRangoExport({ fecha_inicio, fecha_fin }) {
      const queryParams = new URLSearchParams({ fecha_inicio, fecha_fin }).toString();
      return await authenticatedRequest(`/api/asistencias-equipo/export/rango?${queryParams}`);
    }
  };
}
